import type { DependencyGraph } from "../dependency/types.js";
import type {
  TraceConnection,
  TraceElement,
  ResolvedTraceLink,
} from "./types.js";

export interface ResolveTraceLinksOptions {
  traceLinks: Array<{
    id: string;
    from: string;
    to: string;
    resolver: string;
    description?: string;
  }>;
  /** artifactId → TraceElement[] mapping */
  elementsByArtifact: Map<string, TraceElement[]>;
  /** Dependency graph for AST-based resolution. */
  graph?: DependencyGraph;
}

interface NamingCandidate {
  path: string;
}

/**
 * Candidate paths linked by naming/directory convention. Mirrors the logic in
 * `buildTestMapping` (impact/index.ts §8.5).
 */
function namingCandidates(sourcePath: string): NamingCandidate[] {
  const slash = sourcePath.lastIndexOf("/");
  const dir = slash >= 0 ? sourcePath.slice(0, slash) : "";
  const file = slash >= 0 ? sourcePath.slice(slash + 1) : sourcePath;
  const dot = file.lastIndexOf(".");
  const base = dot >= 0 ? file.slice(0, dot) : file;
  const ext = dot >= 0 ? file.slice(dot) : "";
  const dirPrefix = dir ? `${dir}/` : "";

  const candidates: NamingCandidate[] = [
    { path: `${dirPrefix}${base}.test${ext}` },
    { path: `${dirPrefix}${base}.spec${ext}` },
    { path: `${dirPrefix}__tests__/${base}.test${ext}` },
    { path: `${dirPrefix}__tests__/${base}.spec${ext}` },
  ];

  if (dir === "src" || dir.startsWith("src/")) {
    const rest = dir === "src" ? "" : dir.slice("src/".length);
    const restPrefix = rest ? `${rest}/` : "";
    for (const root of ["tests", "__tests__"]) {
      candidates.push(
        { path: `${root}/${restPrefix}${base}.test${ext}` },
        { path: `${root}/${restPrefix}${base}.spec${ext}` },
      );
    }
  }

  return candidates;
}

function resolveAstConnections(
  fromElements: TraceElement[],
  toElements: TraceElement[],
  graph: DependencyGraph | undefined,
): TraceConnection[] {
  if (!graph || fromElements.length === 0 || toElements.length === 0) {
    return [];
  }

  const toPaths = new Set(toElements.map((el) => el.path));
  const connections: TraceConnection[] = [];
  const seen = new Set<string>();

  for (const fromEl of fromElements) {
    for (const edge of graph.edges) {
      if (edge.from !== fromEl.path || !toPaths.has(edge.to)) continue;
      const key = `${fromEl.path}\0${edge.to}`;
      if (seen.has(key)) continue;
      seen.add(key);

      const toEl = toElements.find((el) => el.path === edge.to);
      if (!toEl) continue;

      connections.push({
        from: fromEl,
        to: toEl,
        confidence: 1.0,
        resolver: "ast",
      });
    }
  }

  return connections;
}

function resolveNamingConnections(
  fromElements: TraceElement[],
  toElements: TraceElement[],
): TraceConnection[] {
  const toByPath = new Map(toElements.map((el) => [el.path, el]));
  const connections: TraceConnection[] = [];
  const seen = new Set<string>();

  for (const fromEl of fromElements) {
    for (const candidate of namingCandidates(fromEl.path)) {
      const toEl = toByPath.get(candidate.path);
      if (!toEl) continue;
      const key = `${fromEl.path}\0${toEl.path}`;
      if (seen.has(key)) continue;
      seen.add(key);
      connections.push({
        from: fromEl,
        to: toEl,
        confidence: 0.9,
        resolver: "naming",
      });
    }
  }

  return connections;
}

function resolveCodegenConnections(
  fromElements: TraceElement[],
  toElements: TraceElement[],
): TraceConnection[] {
  if (fromElements.length === 0 || toElements.length === 0) {
    return [];
  }

  const connections: TraceConnection[] = [];
  for (const fromEl of fromElements) {
    for (const toEl of toElements) {
      connections.push({
        from: fromEl,
        to: toEl,
        confidence: 0.8,
        resolver: "codegen",
      });
    }
  }
  return connections;
}

function computeOrphans(
  fromElements: TraceElement[],
  toElements: TraceElement[],
  connections: TraceConnection[],
): ResolvedTraceLink["orphans"] {
  const connectedFrom = new Set(connections.map((c) => c.from.path));
  const connectedTo = new Set(connections.map((c) => c.to.path));

  return {
    from: fromElements.filter((el) => !connectedFrom.has(el.path)),
    to: toElements.filter((el) => !connectedTo.has(el.path)),
  };
}

/**
 * Resolve trace link definitions into concrete file-level connections and
 * orphan elements.
 */
export function resolveTraceLinks(
  options: ResolveTraceLinksOptions,
): ResolvedTraceLink[] {
  const { traceLinks, elementsByArtifact, graph } = options;

  return traceLinks.map((link) => {
    const fromElements = elementsByArtifact.get(link.from) ?? [];
    const toElements = elementsByArtifact.get(link.to) ?? [];

    let connections: TraceConnection[] = [];

    switch (link.resolver) {
      case "ast":
        connections = resolveAstConnections(fromElements, toElements, graph);
        break;
      case "naming":
        connections = resolveNamingConnections(fromElements, toElements);
        break;
      case "codegen":
        connections = resolveCodegenConnections(fromElements, toElements);
        break;
      case "operationId":
        console.warn(
          `[warn] operationId resolver is not implemented for trace link "${link.id}"`,
        );
        connections = [];
        break;
      default:
        console.warn(
          `[warn] Unknown trace resolver "${link.resolver}" for link "${link.id}"`,
        );
        connections = [];
        break;
    }

    return {
      id: link.id,
      from: link.from,
      to: link.to,
      resolver: link.resolver,
      description: link.description,
      connections,
      orphans: computeOrphans(fromElements, toElements, connections),
    };
  });
}
